import { useMemo, useState } from 'react'
import { FaSyncAlt } from 'react-icons/fa'
import { BatchDetail } from './batchDetail'
import styles from './styles.module.scss'
import { BATCH_SOURCE_LABELS, BatchRow, BatchSource, batchStatusTone, formatDateTime } from './types'

type StatusFilter = 'all' | 'running' | 'draft' | 'completed' | 'failed'

interface BatchesTabProps {
    batches: BatchRow[]
    loaded: boolean
    error: string | null
    onRefresh: () => Promise<void> | void
    selectedBatchId: number | null
    onSelectBatch: (batchId: number | null) => void
    onNewBulkSend: () => void
}

const progressOf = (b: BatchRow) => (b.total > 0 ? Math.min(100, Math.round((b.processed / b.total) * 100)) : 0)

export const BatchesTab = ({
    batches,
    loaded,
    error,
    onRefresh,
    selectedBatchId,
    onSelectBatch,
    onNewBulkSend,
}: BatchesTabProps) => {
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        return batches.filter((b) => {
            if (statusFilter !== 'all' && b.status !== statusFilter) return false
            if (!q) return true
            return [b.name, b.template_name, b.created_by, b.source]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q))
        })
    }, [batches, search, statusFilter])

    if (selectedBatchId) {
        return <BatchDetail batchId={selectedBatchId} onBack={() => onSelectBatch(null)} onChanged={onRefresh} />
    }

    return (
        <>
            {error && <div className={styles.errorBanner}>{error}</div>}
            <div className={styles.toolbar}>
                <input
                    className={styles.searchInput}
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Filter by name, template or sender…"
                />
                <select
                    className={styles.toolbarSelect}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                    aria-label="Filter by status"
                >
                    <option value="all">Any status</option>
                    <option value="running">Running</option>
                    <option value="draft">Draft</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                </select>
                <button type="button" className={styles.secondaryBtn} onClick={() => onRefresh()}>
                    <FaSyncAlt /> Refresh
                </button>
                <button type="button" className={styles.primaryBtn} onClick={onNewBulkSend}>
                    New bulk send
                </button>
            </div>

            <p className={styles.resultLine}>
                Showing {filtered.length} of {batches.length} bulk sends — click one to see who has signed.
            </p>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Bulk send</th>
                            <th>Source</th>
                            <th>Status</th>
                            <th>Progress</th>
                            <th>Signed</th>
                            <th>Failed</th>
                            <th>Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={7}>
                                    <div className={styles.emptyState}>
                                        {!loaded
                                            ? 'Loading…'
                                            : batches.length === 0
                                              ? 'No bulk sends yet. Use the Bulk send tab to create one.'
                                              : 'No bulk sends match this filter.'}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filtered.map((b) => {
                                const isEsign = b.delivery_type !== 'attachment'
                                const signed = (b.signed_count || 0) + (b.delivered_count || 0)
                                const progress = progressOf(b)
                                return (
                                    <tr key={b.id} onClick={() => onSelectBatch(b.id)}>
                                        <td>
                                            <div className={styles.stackCell}>
                                                <span className={styles.stackPrimary}>
                                                    {b.name || `Bulk send #${b.id}`}
                                                </span>
                                                <span className={styles.stackSecondary}>
                                                    {b.template_name || 'Template'} ·{' '}
                                                    {b.granularity === 'claim' ? 'per claim' : 'per client'} ·{' '}
                                                    {isEsign ? 'signing links' : 'attachments'}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`${styles.pill} ${styles.muted}`}>
                                                {BATCH_SOURCE_LABELS[b.source as BatchSource] || b.source}
                                            </span>
                                        </td>
                                        <td>
                                            <span
                                                className={`${styles.statusPill} ${styles[batchStatusTone(b.status)]}`}
                                            >
                                                {b.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.stackCell}>
                                                <span className={`${styles.stackPrimary} ${styles.nowrap}`}>
                                                    {b.sent} / {b.total} sent
                                                </span>
                                                {b.status === 'running' && (
                                                    <div className={`${styles.progressBar} ${styles.small}`}>
                                                        <span style={{ width: `${progress}%` }} />
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {isEsign ? (
                                                <span className={`${styles.stackPrimary} ${styles.nowrap}`}>
                                                    {signed} / {b.sent}
                                                </span>
                                            ) : (
                                                <span className={styles.muted}>n/a</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={b.failed > 0 ? styles.toneBad : styles.muted}>
                                                {b.failed}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.stackCell}>
                                                <span className={`${styles.stackPrimary} ${styles.nowrap}`}>
                                                    {formatDateTime(b.created_at)}
                                                </span>
                                                <span className={styles.stackSecondary}>{b.created_by || '—'}</span>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </>
    )
}
